import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ReactComponent as ArrowDownIcon } from 'images/icons/ArrowDown.svg';
import { ReactComponent as ArrowUpIcon } from 'images/icons/ArrowUp.svg';
import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import dayjs from 'dayjs';
import { getLanguageShortText } from '../../../../utilities/languages';
import { mapBooleanToString } from 'utilities/booleanMapper';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  description: string;
  languages: string;
  visible: string;
  comingSoon: string;
  certificate: string;
  createdAt: string;
  updatedAt: string;
};

export const CourseList = observer(() => {
  useSetPageTitle({ title: 'Course List', subtitle: '' });
  const ces = useStores().courseEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);

  const deleteCourse = useCallback(() => {
    ces.deleteCourseAndUpdate();
    setIsModalOn(false);
  }, [ces, setIsModalOn]);

  useEffect(() => {
    ces.coursesList.length === 0 && ces.getCourses();
  }, [ces]);

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ces.coursesList.map((course) => {
        return {
          id: course.id,
          title: course.title.toLowerCase(),
          description: course.descriptions.find((desc) => desc.countryCode === 'en-US')?.text,
          languages: course.languages ? course.languages.map((lng) => getLanguageShortText(lng)).join(', ') : '',
          visible: mapBooleanToString(course.visible),
          comingSoon: mapBooleanToString(course.comingSoon),
          certificate: mapBooleanToString(course.certification),
          createdAt: dayjs(course.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(course.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [ces.coursesList]
  );

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderCreateButton buttonTitle='New course' route={DefaultRoutes.CoursesEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Languages</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell>Coming soon</TableCell>
              <TableCell>Certification</TableCell>
              <TableCell>Date created</TableCell>
              <TableCell>Last edited</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row, index) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <div className='description-tooltip'>
                        {row.title}
                        <Tooltip
                          title={row.description ? row.description : 'No description'}
                          placement='bottom'
                          className='table-cell-tooltip'
                        >
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell className='languages'>{row.languages}</TableCell>
                    <TableCell>{row.visible}</TableCell>
                    <TableCell>{row.comingSoon}</TableCell>
                    <TableCell>{row.certificate}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.CoursesEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className='row-button'
                          style={index === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          onClick={() => (index !== 0 ? ces.updateSortOrder(row.id, true) : {})}
                        >
                          <ArrowUpIcon />
                        </button>
                        <button
                          className='row-button'
                          style={index === ces.coursesList.length - 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                          onClick={() => (index !== ces.coursesList.length - 1 ? ces.updateSortOrder(row.id, false) : {})}
                        >
                          <ArrowDownIcon />
                        </button>
                        <button
                          className='row-button'
                          onClick={() => {
                            ces.selectedCourseId = row.id;
                            setIsModalOn(true);
                          }}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No courses found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deleteCourse} itemName='course' />
    </div>
  );
});

export default CourseList;
